// Tokens tab — refresh token management with nuclear revoke option.

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { Stat } from "@/components/ui/Stat";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";
import { useStepUp } from "@/context/StepUpContext";
import {
  useAdminTokens,
  useAdminTokenStats,
  useRevokeAllTokens,
  useRevokeUserTokens,
} from "@/hooks/useAdmin";
import { clearTokens } from "@/lib/tokens";
import { useDict, fmt } from "@/i18n/LocaleProvider";

const LIMIT = 50;
const GRID = "1.5fr 1.5fr 110px 110px 120px 100px";

function formatErr(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function AdminTokensTab() {
  const tt = useDict().app.adminTokens;
  const { withStepUp } = useStepUp();

  const [filterUser, setFilterUser] = useState("");
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [offset, setOffset] = useState(0);

  const stats = useAdminTokenStats();
  const tokens = useAdminTokens({
    user_id: filterUser || undefined,
    include_revoked: includeRevoked,
    limit: LIMIT,
    offset,
  });
  const revokeUser = useRevokeUserTokens();
  const revokeAll = useRevokeAllTokens();

  const [showNuclear, setShowNuclear] = useState(false);
  const [nuclearConfirm, setNuclearConfirm] = useState("");
  const [nuclearReason, setNuclearReason] = useState("");

  async function handleRevokeUserAll(userId: string, email: string) {
    const ok = await confirm({
      title: tt.revokeUserTitle,
      message: fmt(tt.revokeUserMsg, { email }),
      variant: "danger",
      confirmLabel: tt.revokeSessions,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        const r = await revokeUser.mutateAsync({
          userId,
          reason: "admin panel",
          stepUpToken: token,
        });
        toast.success(fmt(tt.revokedFor, { n: r.revoked_count, email }));
      } catch (e) {
        toast.error(`${tt.revokeFailed} ${formatErr(e)}`);
      }
    });
  }

  async function executeNuclear() {
    if (nuclearConfirm !== "REVOKE ALL") {
      toast.error(tt.mustType);
      return;
    }
    withStepUp(async (token) => {
      try {
        const r = await revokeAll.mutateAsync({
          reason: nuclearReason,
          stepUpToken: token,
        });
        toast.success(
          fmt(tt.nuclearDone, { n: r.revoked_count }),
        );
        setShowNuclear(false);
        setNuclearConfirm("");
        setNuclearReason("");
        window.setTimeout(() => {
          clearTokens();
          window.location.reload();
        }, 2000);
      } catch (e) {
        toast.error(`${tt.nuclearFailed} ${formatErr(e)}`);
      }
    });
  }

  const s = stats.data;
  const d = tokens.data;

  return (
    <div>
      {/* Stats — 2-up on mobile, 4-up from sm+ */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-[10px] mb-3">
          <Card>
            <Stat label={tt.totalTokens} value={s.total} color="rgb(var(--color-content))" />
          </Card>
          <Card>
            <Stat label={tt.active} value={s.active} color="rgb(var(--color-success))" />
          </Card>
          <Card>
            <Stat label={tt.revoked} value={s.revoked} color="rgb(var(--color-muted))" />
          </Card>
          <Card>
            <Stat
              label={tt.usersWithSessions}
              value={s.users_with_active_sessions}
              color="rgb(var(--color-accent))"
            />
          </Card>
        </div>
      )}

      {/* Filters + nuclear button */}
      <Card className="mb-3 !px-[14px] !py-3">
        <div className="flex gap-[10px] items-center flex-wrap">
          <input
            value={filterUser}
            onChange={(e) => {
              setFilterUser(e.target.value);
              setOffset(0);
            }}
            placeholder={tt.filterPlaceholder}
            className="flex-[1_1_200px] px-[10px] py-[7px] bg-bg border border-stroke rounded text-content font-mono text-[11px] outline-none"
          />
          <label className="flex items-center gap-[6px] font-mono text-[11px] text-muted cursor-pointer">
            <input
              type="checkbox"
              checked={includeRevoked}
              onChange={(e) => {
                setIncludeRevoked(e.target.checked);
                setOffset(0);
              }}
              className="cursor-pointer"
              style={{ accentColor: "rgb(var(--color-accent))" }}
            />
            {tt.includeRevoked}
          </label>
          <Button
            variant="danger"
            small
            onClick={() => setShowNuclear((v) => !v)}
          >
            {tt.nuclearBtn}
          </Button>
        </div>
      </Card>

      {/* Nuclear confirmation */}
      {showNuclear && (
        <Card
          className="mb-3"
          style={{ borderColor: "rgb(var(--color-danger))", background: "rgb(var(--color-danger-bg))" }}
        >
          <div className="font-display text-[16px] font-bold text-danger mb-2">
            {tt.nuclearTitle}
          </div>
          <div className="font-mono text-[11px] text-muted mb-[14px] leading-[1.5]">
            {tt.nuclearDesc}
          </div>
          <Input
            label={tt.reasonLabel}
            value={nuclearReason}
            onChange={setNuclearReason}
            placeholder={tt.reasonPlaceholder}
          />
          <div className="font-mono text-[11px] text-danger mb-[6px] font-bold">
            {tt.typeToConfirm}
          </div>
          <input
            value={nuclearConfirm}
            onChange={(e) => setNuclearConfirm(e.target.value)}
            placeholder="REVOKE ALL"
            className="w-full px-3 py-[10px] rounded text-danger font-mono text-[13px] font-bold outline-none mb-3 box-border"
            style={{
              background: "rgb(var(--color-bg))",
              border: "2px solid rgb(var(--color-danger))",
            }}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={executeNuclear}
              disabled={nuclearConfirm !== "REVOKE ALL"}
            >
              {tt.execute}
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setShowNuclear(false);
                setNuclearConfirm("");
                setNuclearReason("");
              }}
            >
              {tt.cancel}
            </Button>
          </div>
        </Card>
      )}

      {/* Tokens list — desktop grid table from md+, stacked cards below. */}
      {!d ? (
        <Loading text={tt.loading} />
      ) : d.tokens.length === 0 ? (
        <Card>
          <div className="text-dim text-center py-6 font-mono text-[11px]">
            {tt.noTokens}
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden flex flex-col gap-3">
            {d.tokens.map((t) => (
              <Card
                key={t.id}
                className="!p-4"
                style={{ opacity: t.revoked ? 0.5 : 1 }}
              >
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-sans text-[14px] font-semibold text-content truncate">
                      {t.user_email}
                    </div>
                    <div className="font-mono text-[10px] text-dim">
                      {tt.idPrefix} {t.user_id}
                    </div>
                  </div>
                  <span
                    className="font-mono text-[10px] shrink-0"
                    style={{ color: t.revoked ? "rgb(var(--color-danger))" : "rgb(var(--color-success))" }}
                  >
                    {t.revoked ? tt.statusRevoked : tt.statusActive}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-muted leading-[1.5]">
                  <div className="truncate">{tt.uaPrefix} {t.user_agent ?? "—"}</div>
                  <div>
                    {tt.ipPrefix} {t.ip_address ?? "—"} · {tt.createdPrefix}{" "}
                    {t.created_at
                      ? new Date(t.created_at).toLocaleDateString()
                      : "?"}
                  </div>
                </div>
                {!t.revoked && (
                  <div className="mt-3">
                    <Button
                      variant="default"
                      onClick={() =>
                        handleRevokeUserAll(t.user_id, t.user_email)
                      }
                      className="!text-danger !border-danger/40 hover:!border-danger/70"
                    >
                      {tt.revokeUser}
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Desktop: grid table */}
          <Card className="!p-0 overflow-hidden hidden md:block">
            <div
              className="grid gap-3 px-4 py-3 bg-bg2 border-b border-stroke font-mono text-[10px] text-muted uppercase tracking-[0.05em] font-semibold items-center"
              style={{ gridTemplateColumns: GRID }}
            >
              <div>{tt.colUser}</div>
              <div>{tt.colUA}</div>
              <div>{tt.colIP}</div>
              <div>{tt.colStatus}</div>
              <div>{tt.colCreated}</div>
              <div />
            </div>
            {d.tokens.map((t) => (
              <div
                key={t.id}
                className="grid gap-3 px-4 py-[10px] border-b border-stroke font-mono text-[11px] items-center"
                style={{
                  gridTemplateColumns: GRID,
                  opacity: t.revoked ? 0.5 : 1,
                }}
              >
                <div className="overflow-hidden">
                  <div className="text-content font-semibold truncate">
                    {t.user_email}
                  </div>
                  <div className="text-dim text-[9px]">{tt.idPrefix} {t.user_id}</div>
                </div>
                <div className="text-muted text-[10px] truncate">
                  {t.user_agent ?? "—"}
                </div>
                <div className="text-muted text-[10px]">{t.ip_address ?? "—"}</div>
                <div>
                  {t.revoked ? (
                    <span className="text-danger text-[10px]">{tt.statusRevoked}</span>
                  ) : (
                    <span className="text-success text-[10px]">{tt.statusActive}</span>
                  )}
                </div>
                <div className="text-muted text-[10px]">
                  {t.created_at
                    ? new Date(t.created_at).toLocaleDateString()
                    : "?"}
                </div>
                <div>
                  {!t.revoked && (
                    <Button
                      variant="ghost"
                      small
                      onClick={() =>
                        handleRevokeUserAll(t.user_id, t.user_email)
                      }
                    >
                      {tt.revokeUser}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {/* Pagination */}
      {d && d.total > LIMIT && (
        <div className="flex justify-center gap-2 mt-3">
          <Button
            variant="default"
            small
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            {tt.prev}
          </Button>
          <span className="font-mono text-[11px] text-muted self-center">
            {offset + 1}-{Math.min(offset + LIMIT, d.total)} {tt.rangeOf} {d.total}
          </span>
          <Button
            variant="default"
            small
            disabled={offset + LIMIT >= d.total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            {tt.next}
          </Button>
        </div>
      )}
    </div>
  );
}
